// The self-hosted instance protects versioned /static extension URLs. Bundle
// replay, logs and error tracking so they never depend on those runtime loads.
import posthog from 'posthog-js/full/no-external'
import type { PostHogInterface } from 'posthog-js'

type PostHogPersistence = {
  register: (properties: Record<string, unknown>) => void
}

function enableBundledLogCapture () {
  const extensions = (window as typeof window & {
    __PosthogExtensions__?: {
      logs?: { initializeLogs?: unknown }
      loadExternalDependency?: (instance: unknown, name: string, callback: (error?: unknown) => void) => void
    }
  }).__PosthogExtensions__

  if (!extensions?.logs?.initializeLogs || extensions.loadExternalDependency) return

  // The no-external bundle contains logs.js already. Resolve that one module
  // locally instead of requesting the self-hosted /static URL.
  extensions.loadExternalDependency = (_instance, name, callback) => {
    callback(name === 'logs' ? undefined : new Error(`PostHog extension ${name} is not bundled`))
  }
}

function forceSessionRecording (instance: PostHogInterface) {
  const persistence = (instance as PostHogInterface & { persistence?: PostHogPersistence }).persistence
  if (!persistence) return

  // This project's remote config currently reports sessionRecording=false.
  // Seed the same shape the SDK persists when replay is enabled server-side,
  // then use the public API to start a fully sampled recording.
  persistence.register({
    $session_recording_remote_config: {
      cache_timestamp: Date.now(),
      enabled: true,
      networkPayloadCapture: { capturePerformance: true },
      canvasRecording: { enabled: false },
      sampleRate: 1,
      minimumDurationMilliseconds: null
    }
  })
  instance.startSessionRecording({ sampling: true })
}

export default defineNuxtPlugin({
  name: 'posthog',
  enforce: 'pre',
  setup (nuxtApp) {
    const config = useRuntimeConfig()
    const key = String(config.public.posthogKey || '')
    const enabled = String(config.public.posthogEnabled) !== 'false'

    if (!key || !enabled) return

    enableBundledLogCapture()

    posthog.init(key, {
      api_host: String(config.public.posthogHost),
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      autocapture: true,
      capture_pageview: 'history_change',
      capture_pageleave: true,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true
      },
      capture_performance: true,
      capture_heatmaps: true,
      capture_dead_clicks: true,
      disable_surveys: true,
      disable_surveys_automatic_display: true,
      disable_product_tours: true,
      disable_conversations: true,
      disable_web_experiments: true,
      disable_session_recording: false,
      enable_recording_console_log: true,
      session_recording: {
        // Booking forms contain personal data; keep the interaction while
        // replacing every entered value in the replay.
        maskAllInputs: true,
        sampleRate: 1
      },
      logs: {
        captureConsoleLogs: true,
        serviceName: 'leonhotel-client',
        environment: import.meta.dev ? 'development' : 'production',
        serviceVersion: String(config.public.posthogVersion || '') || undefined
      },
      // Correlate API errors with the browser session/replay that caused them.
      tracing_headers: ['api.leonhotel.ru'],
      loaded: forceSessionRecording
    })

    // Re-apply after /flags: the current project setting disables replay even
    // though this application explicitly requests it at a 100% sample rate.
    posthog.onFeatureFlags(() => forceSessionRecording(posthog))

    // Vue handles component errors before they necessarily reach window.onerror.
    nuxtApp.hook('vue:error', (error, _instance, info) => {
      posthog.captureException(error, { source: 'vue:error', vue_info: info })
    })
    nuxtApp.hook('app:error', (error) => {
      posthog.captureException(error, { source: 'nuxt:app:error' })
    })

    return { provide: { posthog } }
  }
})
